import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { IconAlertCircle, IconCheck, IconInfo } from '../icons';
import { cx } from './cx';
import s from './Toast.module.css';

type ToastTone = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  text: ReactNode;
  tone: ToastTone;
}

const Ctx = createContext<(text: ReactNode, tone?: ToastTone) => void>(() => {});

export function useToast() {
  return useContext(Ctx);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((text: ReactNode, tone: ToastTone = 'info') => {
    const id = Date.now() + Math.random();
    setItems((t) => [...t.slice(-2), { id, text, tone }]);
    setTimeout(() => setItems((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);
  return (
    <Ctx.Provider value={push}>
      {children}
      <div className={s.region} role="status" aria-live="polite">
        {items.map((t) => {
          const Icon = t.tone === 'success' ? IconCheck : t.tone === 'error' ? IconAlertCircle : IconInfo;
          return (
            <div key={t.id} className={cx(s.toast, s[t.tone])}>
              <span className={s.icon}>
                <Icon size={14} />
              </span>
              <span className={s.text}>{t.text}</span>
            </div>
          );
        })}
      </div>
    </Ctx.Provider>
  );
}
